export const CORNER_SIZE = 15;
export const MIN_CORNER_SIZE = 5;
export const MAX_CORNER_SIZE = 25;

/**
 * Adjusts corner size for an object based on both canvas and object dimensions.
 * This ensures handles don't block visibility of small assets.
 * @param {fabric.Object} obj - The fabric object to adjust
 */
export function adjustCornerSizeForObject(obj) {
  if (!obj || obj.selectable === false || !obj.canvas) return;

  const canvas = obj.canvas;
  const canvasSize = Math.max(canvas.getWidth(), canvas.getHeight());
  const scale = window.currentScaleVal || 1;

  // Base size from canvas (reduced divisor for smaller handles)
  const baseSizeFromCanvas = Math.min(canvasSize / 70, MAX_CORNER_SIZE);

  // Object-size constraint: handles shouldn't exceed 15% of smallest object dimension
  const objWidth = obj.width * (obj.scaleX || 1);
  const objHeight = obj.height * (obj.scaleY || 1);
  const objectMinDimension = Math.min(objWidth, objHeight);
  const maxSizeForObject = objectMinDimension * 0.15;

  // Apply constraints: min 5px, max is lesser of canvas-based or object-based
  let cornerSize = Math.min(baseSizeFromCanvas, maxSizeForObject);
  cornerSize = Math.max(MIN_CORNER_SIZE, cornerSize);

  // Apply zoom scaling
  let finalCornerSize;

  if (scale >= 1) {
    // At zoom >= 100%, use normal scaling
    finalCornerSize = cornerSize * scale;
  } else if (scale >= 0.6) {
    // At zoom 60-99%, use minSize 8
    const minSize = 8;
    const scaledSize = cornerSize * scale;
    finalCornerSize = Math.max(minSize, scaledSize);
  } else if (scale >= 0.4) {
    // At zoom 40-59%, use minSize 10
    const minSize = 10;
    const scaledSize = cornerSize * scale;
    finalCornerSize = Math.max(minSize, scaledSize);
  } else {
    // At zoom < 40%, use minSize 12
    const minSize = 12;
    const scaledSize = cornerSize * scale;
    finalCornerSize = Math.max(minSize, scaledSize);
  }

  // Use set() method for proper Fabric.js property update
  const propsToSet = {
    cornerSize: finalCornerSize
  };

  // For large canvases, also adjust stroke width with normal scaling
  if (canvasSize >= 2000) {
    const baseStrokeWidth = Math.min(canvasSize / 500, 6);
    propsToSet.cornerStrokeWidth = baseStrokeWidth * scale;
  }

  obj.set(propsToSet);
  obj.setCoords();
  obj.dirty = true;

  // Force canvas re-render to show updated corner size
  if (canvas.requestRenderAll) {
    canvas.requestRenderAll();
  }
}

// Attaches scaling event to adjust corner size during scaling
export function scaleControlsCornerSize(fabricObject, size = CORNER_SIZE) {
  return fabricObject.on('scaling', function () {
    // Use the shared adjustCornerSizeForObject function for consistency
    adjustCornerSizeForObject(this);

    // Additional settings for large canvases
    const canvas = this.canvas;
    const canvasSize = canvas ? Math.max(canvas.getWidth(), canvas.getHeight()) : 1000;
    const scale = window.currentScaleVal || 1;

    // Calculate proportional stroke width for large canvases
    const strokeWidth = canvasSize >= 2000 ? Math.min(canvasSize / 500, 6) : 2;
    this.cornerStrokeWidth = strokeWidth * scale;

    // Set borderDashArray to false for solid border (in Fabric 4.x)
    if (canvasSize >= 2000) {
      this.borderDashArray = false;
      // Increase border thickness for better visibility
      this.borderScaleFactor = Math.min(canvasSize / 2000, 2);
    }

    // In Fabric.js 4.x, modify the mtr (rotation) control directly for this object
    if (canvasSize >= 2000 && this.controls && this.controls.mtr) {
      // Calculate offset to position rotation handle above object
      const mtrOffset = Math.min(canvasSize / 80, 50) * scale;

      const originalMtr = this.controls.mtr;
      this.controls.mtr = new fabric.Control({
        x: 0,
        y: -0.5,  // Keep at -0.5 for top position
        offsetY: -mtrOffset,  // Use negative offset to move it away from top
        cursorStyle: 'crosshair',
        actionHandler: originalMtr.actionHandler || fabric.controlsUtils.rotationWithSnapping,
        actionName: 'rotate',
        render: originalMtr.render,
        cornerSize: originalMtr.cornerSize,
        withConnection: false  // Disable built-in connection and use custom render
      });

      // Store custom render that draws connection line correctly
      const that = this;
      this.controls.mtr.render = function(ctx, left, top, styleOverride, fabricObject) {
        ctx.save();

        // Draw the connection line from top edge to handle
        ctx.strokeStyle = '#4CAF50';
        ctx.lineWidth = 2 / fabricObject.canvas.getZoom();
        ctx.beginPath();

        // Use Fabric.js calculated coordinates (oCoords.mt = middle-top point)
        // This is more reliable than manual calculation as it handles all transformations
        let topEdgeX, topEdgeY;

        if (fabricObject.oCoords && fabricObject.oCoords.mt) {
          // Use pre-calculated middle-top coordinates
          topEdgeX = fabricObject.oCoords.mt.x;
          topEdgeY = fabricObject.oCoords.mt.y;
        } else {
          // Fallback to manual calculation if oCoords not available
          const center = fabricObject.getCenterPoint();
          const angle = fabric.util.degreesToRadians(fabricObject.angle || 0);
          const offsetFromCenterToTop = fabricObject.height * fabricObject.scaleY / 2;

          topEdgeX = center.x + (0 * Math.cos(angle) - (-offsetFromCenterToTop) * Math.sin(angle));
          topEdgeY = center.y + (0 * Math.sin(angle) + (-offsetFromCenterToTop) * Math.cos(angle));
        }

        // Validate line length to prevent rendering anomalies
        const lineLength = Math.sqrt(Math.pow(left - topEdgeX, 2) + Math.pow(top - topEdgeY, 2));
        const maxReasonableLength = Math.max(fabricObject.canvas.getWidth(), fabricObject.canvas.getHeight());

        if (lineLength > maxReasonableLength) {
          // Skip drawing the line if it's unreasonably long (likely a calculation error)
          ctx.restore();
          // Still draw the rotation icon
          if (originalMtr.render) {
            originalMtr.render.call(this, ctx, left, top, styleOverride, fabricObject);
          }
          return;
        }

        // Draw line from top edge to handle position
        ctx.moveTo(topEdgeX, topEdgeY);
        ctx.lineTo(left, top);
        ctx.stroke();
        ctx.restore();

        // Draw the rotation icon using original render or default
        if (originalMtr.render) {
          originalMtr.render.call(this, ctx, left, top, styleOverride, fabricObject);
        } else {
          // Default rotation handle rendering
          ctx.save();
          ctx.fillStyle = fabricObject.cornerColor || 'black';
          ctx.strokeStyle = fabricObject.cornerStrokeColor || 'black';
          ctx.lineWidth = 1 / fabricObject.canvas.getZoom();
          ctx.beginPath();
          ctx.arc(left, top, fabricObject.cornerSize / 2, 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();
          ctx.restore();
        }
      };
    }

    this.setCoords();
    if (this.canvas) {
      this.canvas.renderAll();
    }
  });
}
